import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import type { ApolloClient } from "@apollo/client";
import { Col, Form, Row } from "react-bootstrap";
import Button from "components/Button";
import { activatePayrollIntegration } from "actions/employerActions";
import { employerConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import Alert from "components/Alert";
import { banksSelector } from "store/selectors/bank";
import type { RootState } from "types/state";

const isLoadingSelector = createLoadingSelector([
  employerConstants.ACTIVATE_PAYROLL_INTEGRATION,
  employerConstants.ACKNOWLEDGE_RECURRING_DEBITS,
]);

const errorSelector = createErrorSelector([
  employerConstants.ACTIVATE_PAYROLL_INTEGRATION,
  employerConstants.ACKNOWLEDGE_RECURRING_DEBITS,
]);

const mapStateToProps = (state: RootState) => {
  const employerBankAccounts = banksSelector(state) || [];
  const bankAccountNumbers = employerBankAccounts.map((bank) => bank.accountId);
  return {
    isLoading: isLoadingSelector(state),
    error: errorSelector(state),
    bankAccountNumbers: bankAccountNumbers.join(", "),
  };
};

const mapDispatchToProps = {
  activatePayrollIntegration,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onClick?: () => void;
  btnLabel?: string;
  fromModal?: boolean;
}

type Props = WithApolloClient<OwnProps> & PropsFromRedux;

class PayrollIntegratedACHDisclaimer extends React.PureComponent<Props> {
  static defaultProps = {
    btnLabel: "Submit",
  };

  handleSubmit = () => {
    if (this.props.onClick) {
      this.props.onClick();
      return;
    }
    void this.props.activatePayrollIntegration(
      this.props.client as ApolloClient<object>
    );
  };

  render() {
    return (
      <div className="ach-disclaimer-card" style={{ paddingLeft: 26 }}>
        <Form.Group as={Row} className="mb-4">
          <Col sm={12}>
            <div>
              {this.props.fromModal ? (
                <>
                  <p>
                    By clicking continue, you authorize Icon Financial Services,
                    LLC (&quot;Icon&quot;), acting as Originating Depository
                    Financial Institution via Cross River Bank (ODFI), to
                    initiate ACH debit and credit entries to your Company&apos;s
                    designated bank account, and to make correcting or adjusting
                    entries, if necessary.
                  </p>
                  <p>
                    You represent that you are the authorized holder of the
                    account you are linking and authorize Icon to verify the
                    account information using commercially reasonable methods.
                    You further authorize Icon to debit your linked account(s)
                    on a recurring basis according to you payroll schedule. If
                    the debit processing date falls on a weekend, US federal
                    holidays, or after 5:00 PM Eastern, requests will be
                    processed on the next business day.
                  </p>
                  <p>
                    You agree that ACH transactions initiated under this
                    authorization will comply with NACHA Rules and applicable
                    U.S. laws and acknowledge that this is a
                    business-to-business authorization not subject to consumer
                    protections. If you believe there is an error or must revoke
                    authorization you must provide a written request to
                    support@iconsavingsplan.com within two (2) business days.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    By clicking submit, you authorize Icon Financial Services,
                    LLC (&quot;Icon&quot;), acting as Originating Depository
                    Financial Institution via Cross River Bank (ODFI), to
                    initiate ACH debit and credit entries to your Company’s
                    designated bank account, and to make correcting or adjusting
                    entries, if necessary.
                  </p>
                  <p>
                    You represent that you are the authorized holder of the
                    account provided and authorize Icon to verify the account
                    information using commercially reasonable methods. You
                    further authorize Icon to debit your account(s) ending in{" "}
                    {this.props.bankAccountNumbers} periodically according to
                    your payroll schedule and the posted payroll debits in your
                    linked payroll system. If the processing date falls on a
                    weekend, US federal holidays, or after 5:00 PM Eastern,
                    requests will be processed on the next business day.
                  </p>
                  <p>
                    You agree that ACH transactions initiated under this
                    authorization will comply with NACHA Rules and applicable
                    U.S. laws and acknowledge that this is a
                    business-to-business authorization not subject to consumer
                    protections. This authorization remains in effect until Icon
                    receives Company’s written revocation. If you believe there
                    is an error or must revoke authorization you must provide a
                    written request to support@iconsavingsplan.com at least two
                    (2) business days prior to any scheduled transaction.
                    Revoking this authorization does not cancel any obligations
                    Company owes.
                  </p>
                </>
              )}
            </div>
          </Col>
        </Form.Group>

        <Row>
          {this.props.error && <Alert type="error" msg={this.props.error} />}
          <Col sm={12}>
            <Button
              onClick={this.handleSubmit}
              color="primary"
              name="action"
              size="sm"
              btnLabel={this.props.btnLabel}
              loading={this.props.isLoading}
              withArrow={true}
            />
          </Col>
        </Row>
      </div>
    );
  }
}

export default connector(withApollo<Props>(PayrollIntegratedACHDisclaimer));
